import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  convertColorToHSL,
  generateThemeColor,
} from "senf-atomic-design-system/src/styles/helpers";
import { useAuthContext } from "senf-shared/index";
import Button from "../../../../core/button/Button";
import FileDrop from "../../../../core/fileDrop/FileDrop";
import InfoToast from "../../../../molecules/infoToast/InfoToast";
import { useModals } from "../../../../molecules/modalStack/ModalProvider";
import Tabs from "../../../../molecules/tabs/Tabs";
import BimStyleConditions from "./BimStyleConditions";
import BimUploader, { type SavedAsset } from "./BimUploader";
import AttributeDisplay from "./cesium/components/AttributeDisplay";
import { UploadFileDrop } from "./GeoJSONUploader";
import GisDataset from "./GisDataset";
import SavedModels from "./SavedModels";

// Define the BimModel interface that includes styles
interface BimModel extends SavedAsset {
  styles?: Array<{
    property: string;
    value: string;
    color: string;
    opacity: number;
  }>;
}

function fixEncoding(incorrectText) {
  const bytes = [];
  for (let i = 0; i < incorrectText.length; i++) {
    bytes.push(incorrectText.charCodeAt(i));
  }
  const utf8Decoder = new TextDecoder("utf-8");
  const byteArray = new Uint8Array(bytes);
  const correctText = utf8Decoder.decode(byteArray);
  return correctText;
}

function isEncodingIncorrect(text) {
  // You can customize this function to better identify incorrectly encoded strings
  return text.includes("Ã");
}

const GisUploadBuilder = ({ formik, setDrawMode, initialColor }) => {
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const { openModal, closeModal, openDrawer } = useModals();

  const [selectedAttributes, setSelectedAttributes] = useState<{
    [key: string]: any;
  } | null>(null);
  const [isAttributeDisplayVisible, setIsAttributeDisplayVisible] =
    useState(false);
  const [cursorPosition, setCursorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [highlightedCondition, setHighlightedCondition] = useState<{
    property: string;
    value: string;
  } | null>(null);
  const [dataIndex, setDataIndex] = useState("GIS");
  // Listen for object clicks to update selected attributes
  useEffect(() => {
    const handleObjectClick = (event: CustomEvent) => {
      setSelectedAttributes(event.detail.attributes || event.detail);
      setCursorPosition(event.detail.cursorPosition || null);
      setIsAttributeDisplayVisible(true);
    };

    const handleCesiumObjectClick = (event: CustomEvent) => {
      setSelectedAttributes(event.detail.attributes || event.detail);
      setCursorPosition(event.detail.cursorPosition || null);
      setIsAttributeDisplayVisible(true);
    };

    const handleCesiumEmptyClick = () => {
      // Clear selected attributes when clicking on empty space
      setSelectedAttributes(null);
      setCursorPosition(null);
      setIsAttributeDisplayVisible(false);
    };

    window.addEventListener("objectClick", handleObjectClick as EventListener);
    window.addEventListener(
      "cesiumObjectClick",
      handleCesiumObjectClick as EventListener,
    );
    window.addEventListener(
      "cesiumEmptyClick",
      handleCesiumEmptyClick as EventListener,
    );

    return () => {
      window.removeEventListener(
        "objectClick",
        handleObjectClick as EventListener,
      );
      window.removeEventListener(
        "cesiumObjectClick",
        handleCesiumObjectClick as EventListener,
      );
      window.removeEventListener(
        "cesiumEmptyClick",
        handleCesiumEmptyClick as EventListener,
      );
    };
  }, []);

  // Get current conditions from all BIM models
  const currentConditions =
    formik.values.bimModels?.flatMap((model: BimModel) => model.styles || []) ||
    [];

  // Handle style conditions change
  const handleStyleConditionsChange = useCallback(
    (
      updatedConditions: Array<{
        property: string;
        value: string;
        color: string;
        opacity: number;
      }>,
    ) => {
      // Initialize bimModels array if it doesn't exist
      if (!formik.values.bimModels) {
        formik.setFieldValue("bimModels", []);
      }

      // Get current BIM models
      const currentModels = formik.values.bimModels || [];

      // Update the first BIM model with the new conditions
      if (currentModels.length > 0) {
        const updatedModels = currentModels.map(
          (model: BimModel, index: number) => {
            if (index === 0) {
              return {
                ...model,
                styles: updatedConditions,
              };
            }
            return model;
          },
        );

        formik.setFieldValue("bimModels", updatedModels);
      }

      // Dispatch the styleConditionsChange event to apply styles in Cesium
      window.dispatchEvent(
        new CustomEvent("styleConditionsChange", {
          detail: { conditions: updatedConditions },
        }),
      );
    },
    [formik],
  );

  // Handle attribute click to create style condition
  const handleAttributeClick = useCallback(
    (property: string, value: any) => {
      // Initialize bimModels array if it doesn't exist
      if (!formik.values.bimModels) {
        formik.setFieldValue("bimModels", []);
      }

      // Get current BIM models
      const currentModels = formik.values.bimModels || [];

      // Create new condition
      const newCondition = {
        property,
        value: String(value),
        color: "#ffffff",
        opacity: 1.0,
      };

      // Find the first BIM model and add the condition to its styles
      if (currentModels.length > 0) {
        const updatedModels = currentModels.map(
          (model: BimModel, index: number) => {
            if (index === 0) {
              // Add to first model
              const existingStyles = model.styles || [];
              const conditionExists = existingStyles.some(
                (c) =>
                  c.property === newCondition.property &&
                  c.value === newCondition.value,
              );

              if (!conditionExists) {
                return {
                  ...model,
                  styles: [...existingStyles, newCondition],
                };
              }
            }
            return model;
          },
        );

        formik.setFieldValue("bimModels", updatedModels);
      }

      // Set highlighted condition and scroll to top
      setHighlightedCondition({ property, value: String(value) });

      // Switch to styles tab
      setDataIndex("BIM");

      // Close the AttributeDisplay
      setIsAttributeDisplayVisible(false);
      setSelectedAttributes(null);
      setCursorPosition(null);
    },
    [formik],
  );

  // Handle BIM assets completed and ready to save to Formik
  const handleBimAssetsComplete = useCallback(
    (assets: SavedAsset[]) => {
      // Convert all completed assets to BimModels
      const newBimModels: BimModel[] = assets.map((asset) => ({
        ...asset,
        styles: [],
      }));

      // Get current models and append new ones (avoiding duplicates)
      const currentModels = formik.values.bimModels || [];
      const existingAssetIds = new Set(
        currentModels.map((model) => model.assetId),
      );

      const uniqueNewModels = newBimModels.filter(
        (model) => !existingAssetIds.has(model.assetId),
      );

      // Append new models to existing ones
      formik.setFieldValue("bimModels", [...currentModels, ...uniqueNewModels]);

      // Switch to BIM tab after successful upload
      if (uniqueNewModels.length > 0) {
        setDataIndex("BIM");
      }
    },
    [formik],
  );

  // Handle removing BIM asset
  const handleRemoveBimAsset = (assetId: number) => {
    const currentModels = formik.values.bimModels || [];
    const updatedModels = currentModels.filter(
      (model: BimModel) => model.assetId !== assetId,
    );
    formik.setFieldValue("bimModels", updatedModels);
  };

  const handleUpload = (data, type, indexAddition = 0) => {
    setDrawMode({ mode: "draw", drawType: null });

    if (type !== "geojson") {
      const dataIndex = formik.values.gisData.length; // Get the current length of the array
      formik.setFieldValue(`gisData[${dataIndex}].orginialType`, type);
    }

    if (["geojson", "csv", "json", "zip"].includes(type)) {
      const dataIndex = formik.values.gisData.length + indexAddition; // Get the current length of the array

      formik.setFieldValue(`gisData[${dataIndex}].type`, "geojson");
      formik.setFieldValue(`gisData[${dataIndex}].layerTitle`, data.title);
      formik.setFieldValue(`gisData[${dataIndex}].selectedAttribute`, "none");

      const prevFeaturesLength =
        formik.values.gisData?.[dataIndex]?.data?.features?.length || 0;

      const updatedFeatures = data?.data?.features.map((feature, index) => {
        const updatedProperties = { ...feature.properties };

        // Iterate through each property and fix encoding if necessary
        for (const key in updatedProperties) {
          if (
            typeof updatedProperties[key] === "string" &&
            isEncodingIncorrect(updatedProperties[key])
          ) {
            updatedProperties[key] = fixEncoding(updatedProperties[key]);
          }
        }

        return {
          ...feature,
          properties: {
            ...updatedProperties,
            color: initialColor,
            labelColor: generateThemeColor(convertColorToHSL(initialColor))
              ?.color["800"],
            number: prevFeaturesLength + index + 1,
            datasetIndex: dataIndex,
            tooltipLabel: "none",
          },
        };
      });

      const modifiedData = {
        type: "FeatureCollection",
        features: [
          ...(formik.values.gisData?.[dataIndex]?.data?.features || []),
          ...updatedFeatures,
        ],
      };

      setTimeout(() => {
        formik.setFieldValue(`gisData[${dataIndex}].data`, modifiedData);
        formik.setFieldValue(`gisData[${dataIndex}].title`, data.title);

        // Switch to GIS tab after successful upload
        setDataIndex("GIS");
      }, 100);
    }
  };

  return (
    <div className="flex flex-col gap-5 pb-5">
      <UploadFileDrop
        // text={`${t("util:upload", {
        //   val: `${t("global:geodata")}`,
        // })} (WGS84 / UTM zone 32 / EPSG::25832)`}
        handleUploadGeoJSON={handleUpload}
        // fileTypes={["geojson", "json", "csv", "zip"]}
        fileTypes={["geojson", "json", "csv", "zip", "ifc"]}
        fileTypesText="GeoJSON, JSON, CSV, Shapefiles-zip, IFC"
        handleBimAssetsComplete={handleBimAssetsComplete}
        formik={formik}
      />

      {formik.values.gisData?.length > 1 || formik.values.bimModels?.length ? (
        <div>
          <div className="border-b mb-5 flex justify-between">
            <Tabs
              classNames={{
                wrapper: "h-14",
              }}
              active={dataIndex}
              setActive={setDataIndex}
              tabs={(() => {
                const tabs = [
                  formik.values.gisData?.length > 1
                    ? {
                        text: "GIS",
                        tab: "GIS",
                      }
                    : null,
                  formik.values.bimModels?.length
                    ? {
                        text: "BIM",
                        tab: "BIM",
                      }
                    : null,
                ].filter(Boolean);
                console.log("Available tabs:", tabs, "dataIndex:", dataIndex);
                return tabs;
              })()}
            />
            {/* <Button
                text={t("util:upload", {
                  val: "Modell",
                })}
                size="md"
                leadingIcon="plus-md"
                classNameWrapper="mt-3"
                onClick={openBimUploadModal}
              /> */}
          </div>
          <div>
            {dataIndex === "GIS" && (
              <>
                {formik.values.gisData?.map((data, index) => {
                  if (index === 0) return;

                  return (
                    <div className="mb-5" key={index}>
                      <GisDataset
                        formik={formik}
                        index={index}
                        initialColor={initialColor}
                      />
                    </div>
                  );
                })}
              </>
            )}

            {dataIndex === "BIM" && (
              <div>
                <InfoToast
                  text="Klicken Sie auf ein beliebiges 3D-Objekt in der Szene, um dessen Eigenschaften anzuzeigen und Stilbedingungen zu erstellen."
                  className="my-4"
                  closeable
                />
                <SavedModels
                  assets={formik.values.bimModels as BimModel[]}
                  onRemove={handleRemoveBimAsset}
                />
                <BimStyleConditions
                  conditions={currentConditions}
                  onStyleConditionsChange={handleStyleConditionsChange}
                  highlightedCondition={highlightedCondition}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Attribute Display */}
      <AttributeDisplay
        attributes={selectedAttributes}
        visible={isAttributeDisplayVisible}
        cursorPosition={cursorPosition}
        onClose={() => {
          setIsAttributeDisplayVisible(false);
          setSelectedAttributes(null);
          setCursorPosition(null);
        }}
        onAttributeClick={handleAttributeClick}
      />
    </div>
  );
};

export default GisUploadBuilder;
